import { IconNames } from 'ui/theme';
import { Routes as MaterialsRoutes } from 'ui/modules/materials';
import { Routes as SetupRoutes } from 'ui/modules/setup';
import { Routes as PurchasingRoutes } from 'ui/modules/purchasing';
import { Routes as SalesRoutes } from 'ui/modules/sales';
import { Routes as ReportsRoutes } from 'ui/modules/reports';
import { Routes as AccountingRoutes } from 'ui/modules/accounting';
import { Routes as HomeRoutes } from 'ui/modules/home';
import { Routes as CommerceRoutes } from 'ui/modules/commerce';
import { Routes as IntegrationsRoutes } from 'ui/modules/integrations';
import { Routes as RemoteSupportRoutes } from 'ui/modules/remotesupport';

import { PermissionType } from 'services/permissions';
import { FeatureAccessKey } from 'types/featureAccess';
import LeaveSandbox from './_sandbox/LeaveSandbox';
import ClearSandboxData from './_sandbox/ClearSandboxData';
import PopulateSandboxSampleData from './_sandbox/PopulateSandboxSampleData';
import PopulateSandboxAccountData from './_sandbox/PopulateSandboxAccountData';
import { DrawerItemType } from './DrawerItem';
import { concatRoute } from 'helpers/routes';

export const DRAWER_WIDTH = 200;
export const DRAWER_SMALL_WIDTH = 57;
export const APPBAR_SPACER_HEIGHT = 56;

/**
 * Array of items that will be rendered in app drawer.
 * {
 *  text: Text that is displayed
 *  icon: Icon that is used
 *  route: Route used for identifying parent. Never used as link.
 *  subitems [
 *    {
 *      text: Text that is display
 *      route: Route used for redirect when item is clicked
 *    }
 *  ]
 * }
 */
// Type definition for feature access configuration
type FeatureAccessConfig = Record<FeatureAccessKey, boolean>;

export const DRAWER_ITEMS = ({
  quickbooksConnected,
  isTestEnv,
  hasConnectedShippingChannels,
  xeroConnected,
  isMultiCurrencyVisible,
  showAiInsights,
  showTemplates,
  showTransferOrders,
  featureAccess,
}: Record<
  | 'quickbooksConnected'
  | 'isTestEnv'
  | 'hasConnectedShippingChannels'
  | 'xeroConnected'
  | 'isMultiCurrencyVisible'
  | 'showAiInsights'
  | 'showTemplates'
  | 'showTransferOrders',
  boolean
> & { featureAccess: FeatureAccessConfig }): DrawerItemType[] => [
  {
    text: 'Sample Database',
    icon: IconNames.MenuSandbox,
    subitems: [
      {
        text: '',
        route: null,
        component: PopulateSandboxAccountData,
        visible: isTestEnv,
      },
      {
        text: '',
        route: null,
        component: PopulateSandboxSampleData,
        visible: isTestEnv,
      },
      {
        text: '',
        route: null,
        component: ClearSandboxData,
        visible: isTestEnv,
      },
      {
        text: '',
        route: null,
        component: LeaveSandbox,
        visible: isTestEnv,
      },
    ],
    route: null,
    visible: isTestEnv,
  },
  {
    text: 'Home',
    icon: IconNames.MenuHome,
    subitems: [
      {
        text: 'Dashboard',
        permissions: [PermissionType.DashboardView],
        route: HomeRoutes.DashboardPage,
      },
      {
        text: 'Flow',
        route: HomeRoutes.FlowPage,
        permissions: [],
      },
    ],
    route: null,
  },
  {
    text: 'Materials',
    icon: IconNames.MenuMaterials,
    permissions: [PermissionType.InventoryView, PermissionType.ItemsView],
    subitems: [
      {
        text: 'Items',
        route: concatRoute(
          MaterialsRoutes.MaterialsRouter,
          MaterialsRoutes.ItemsPage
        ),
        permissions: [PermissionType.ItemsView],
      },
      {
        text: 'Inventory',
        route: concatRoute(
          MaterialsRoutes.MaterialsRouter,
          MaterialsRoutes.TrackingPage
        ),
        permissions: [PermissionType.InventoryView],
      },
      {
        text: 'Transfer Orders',
        route: concatRoute(
          MaterialsRoutes.MaterialsRouter,
          MaterialsRoutes.TransferOrdersPage
        ),
        permissions: [PermissionType.InventoryView],
        visible: showTransferOrders,
      },
    ],
    route: null,
  },
  {
    text: 'Sales',
    icon: IconNames.MenuSales,
    permissions: [
      PermissionType.CustomersView,
      PermissionType.PricingRulesView,
      PermissionType.SalesOrderView,
      PermissionType.PickingView,
      PermissionType.ShippingView,
    ],
    visible: featureAccess.canAccessSales || featureAccess.canAccessCustomers,
    subitems: [
      {
        text: 'Sales Orders',
        route: concatRoute(SalesRoutes.SalesRouter, SalesRoutes.SalesOrderPage),
        permissions: [PermissionType.SalesOrderView],
        visible: featureAccess.canAccessSales,
      },
      {
        text: 'Picking',
        route: concatRoute(SalesRoutes.SalesRouter, SalesRoutes.PickingPage),
        permissions: [PermissionType.PickingView],
        visible: featureAccess.canAccessSales,
      },
      {
        text: 'Shipping',
        route: concatRoute(SalesRoutes.SalesRouter, SalesRoutes.ShippingPage),
        permissions: [PermissionType.ShippingView],
        visible: featureAccess.canAccessSales,
      },
      {
        text: 'Customers',
        route: concatRoute(SalesRoutes.SalesRouter, SalesRoutes.CustomersPage),
        permissions: [PermissionType.CustomersView],
        visible: featureAccess.canAccessCustomers,
      },
      {
        text: 'Pricing Rules',
        route: concatRoute(
          SalesRoutes.SalesRouter,
          SalesRoutes.PricingRulesPage
        ),
        permissions: [PermissionType.PricingRulesView],
        visible: featureAccess.canAccessSales,
      },
    ],
    route: null,
  },
  {
    text: 'Purchasing',
    icon: IconNames.MenuPurchasing,
    permissions: [
      PermissionType.VendorsView,
      PermissionType.PurchaseOrderView,
      PermissionType.ReceivingView,
    ],
    visible: featureAccess.canAccessPurchasing,
    subitems: [
      {
        text: 'Purchase Orders',
        route: concatRoute(
          PurchasingRoutes.PurchasingRouter,
          PurchasingRoutes.PurchaseOrderPage
        ),
        permissions: [PermissionType.PurchaseOrderView],
      },
      {
        text: 'Receiving',
        route: concatRoute(
          PurchasingRoutes.PurchasingRouter,
          PurchasingRoutes.ReceivingPage
        ),
        permissions: [PermissionType.ReceivingView],
      },
      {
        text: 'Vendors',
        route: concatRoute(
          PurchasingRoutes.PurchasingRouter,
          PurchasingRoutes.VendorsPage
        ),
        permissions: [PermissionType.VendorsView],
      },
    ],
    route: null,
  },
  {
    text: 'Accounting',
    icon: IconNames.MenuAccounting,
    visible: featureAccess.canAccessAccounting,
    subitems: [
      {
        text: 'Chart of Accounts',
        route: concatRoute(
          AccountingRoutes.AccountingRouter,
          AccountingRoutes.LedgersPage
        ),
      },
      {
        text: 'Classes',
        route: concatRoute(
          AccountingRoutes.AccountingRouter,
          AccountingRoutes.ClassesPage
        ),
      },
      {
        text: 'Invoices',
        route: concatRoute(
          AccountingRoutes.AccountingRouter,
          AccountingRoutes.InvoicesPage
        ),
        visible: featureAccess.canAccessSales,
      },
      {
        text: 'QuickBooks Online',
        route: concatRoute(
          AccountingRoutes.AccountingRouter,
          AccountingRoutes.AccountingSettingsPage
        ),
        visible: quickbooksConnected,
      },
      {
        text: 'Xero',
        route: concatRoute(
          AccountingRoutes.AccountingRouter,
          AccountingRoutes.AccountingSettingsPage
        ),
        visible: xeroConnected,
      },
    ],
    route: null,
    permissions: [PermissionType.AccountingView],
  },
  {
    text: 'Reporting',
    icon: IconNames.MenuReports,
    route: null,
    permissions: [
      PermissionType.ReportVendorFulFilmentHistory,
      PermissionType.ReportTotalItemSales,
      PermissionType.ReportShippingInvoice,
      PermissionType.ReportSalesOrder,
      PermissionType.ReportReorder,
      PermissionType.ReportReceivingList,
      PermissionType.ReportPurchaseOrder,
      PermissionType.ReportPick,
      PermissionType.ReportPackingList,
      PermissionType.ReportItemsPriceList,
      PermissionType.ReportInventoryValuationSummary,
      PermissionType.ReportInventoryOnHand,
      PermissionType.ReportCycleCount,
      PermissionType.ReportCustomerHistory,
      PermissionType.ReportMargin,
      PermissionType.ReportConsumption,
      PermissionType.ReportItemPriceList,
      PermissionType.ReportBarcode,
      PermissionType.ReportInventoryAvailability,
      PermissionType.ReportInventoryEventHistory,
      PermissionType.ReportItemBarcodeOneOff,
      PermissionType.ReportLocationBarcode,
      PermissionType.ReportOnHandByExpiration,
      PermissionType.ReportSalesOrderSummary,
      PermissionType.ReportPurchaseOrderSummary,
      PermissionType.ReportBOL,
    ],
    subitems: [
      {
        text: 'Reports',
        route: concatRoute(
          ReportsRoutes.ReportsRouter,
          ReportsRoutes.ReportsPage
        ),
        permissions: [
          PermissionType.ReportInventoryValuationSummary,
          PermissionType.ReportInventoryOnHand,
          PermissionType.ReportCycleCount,
          PermissionType.ReportConsumption,
          PermissionType.ReportBarcode,
          PermissionType.ReportInventoryAvailability,
          PermissionType.ReportInventoryEventHistory,
          PermissionType.ReportItemBarcodeOneOff,
          PermissionType.ReportLocationBarcode,
          PermissionType.ReportOnHandByExpiration,
          PermissionType.ReportBOL,
        ],
      },
      {
        text: 'AI Insights',
        route: concatRoute(
          ReportsRoutes.ReportsRouter,
          ReportsRoutes.InsightsPage
        ),
        permissions: [PermissionType.AiInsightsViewEdit],
        visible: showAiInsights,
      },
      {
        text: 'Templates',
        route: concatRoute(
          ReportsRoutes.ReportsRouter,
          ReportsRoutes.TemplatesPage
        ),
        visible: showTemplates && featureAccess.canAccessTemplates,
      },
    ],
  },
  {
    text: 'Integrations',
    icon: IconNames.MenuIntegrations,
    route: null,
    visible: featureAccess.canAccessIntegrations,
    subitems: [
      {
        text: 'Channels',
        route: concatRoute(
          CommerceRoutes.CommerceRouter,
          CommerceRoutes.MyChannelsPage
        ),
      },
      {
        text: 'Market',
        route: concatRoute(
          IntegrationsRoutes.IntegrationsRouter,
          IntegrationsRoutes.IntegrationsPage
        ),
      },
      {
        text: 'Shipping',
        route: concatRoute(
          IntegrationsRoutes.IntegrationsRouter,
          IntegrationsRoutes.ShippingPage
        ),
        visible: hasConnectedShippingChannels,
      },
    ],
  },
  {
    placement: 'top',
    text: 'Settings',
    icon: IconNames.MenuSettings,
    permissions: [
      PermissionType.UomView,
      PermissionType.LocationView,
      PermissionType.UserView,
      PermissionType.PermissionView,
      PermissionType.PaymentTermsView,
      PermissionType.RepresentativesView,
      PermissionType.TaxRatesView,
      PermissionType.CarriersView,
      PermissionType.SettingsCompanyView,
      PermissionType.SettingsEmailingView,
      PermissionType.SettingsPickingView,
      PermissionType.SettingsSalesOrderView,
      PermissionType.SettingsTrackingView,
      PermissionType.SettingsShippingView,
      PermissionType.SettingsAccountMappingView,
      PermissionType.SettingsShippingView,
    ],
    subitems: [
      {
        text: 'UOMs',
        route: concatRoute(
          SetupRoutes.SetupRouter,
          SetupRoutes.UnitOfMeasurementPage
        ),
        permissions: [PermissionType.UomView],
      },
      {
        text: 'Locations',
        route: concatRoute(SetupRoutes.SetupRouter, SetupRoutes.LocationPage),
        permissions: [PermissionType.LocationView],
      },
      {
        text: 'Users',
        route: concatRoute(SetupRoutes.SetupRouter, SetupRoutes.UsersPage),
        permissions: [PermissionType.UserView],
        visible: !isTestEnv,
      },
      {
        text: 'Permissions',
        route: concatRoute(
          SetupRoutes.SetupRouter,
          SetupRoutes.PermissionsPage
        ),
        permissions: [PermissionType.PermissionView],
      },
      {
        text: 'Payment Terms',
        route: concatRoute(
          SetupRoutes.SetupRouter,
          SetupRoutes.PaymentTermsPage
        ),
        permissions: [PermissionType.PaymentTermsView],
        visible: featureAccess.canAccessPaymentTerms,
      },
      {
        text: 'Currency',
        route: concatRoute(SetupRoutes.SetupRouter, SetupRoutes.CurrencyPage),
        permissions: [PermissionType.CurrencyView],
        visible: isMultiCurrencyVisible,
      },
      {
        text: 'Representatives',
        route: concatRoute(
          SetupRoutes.SetupRouter,
          SetupRoutes.RepresentativesPage
        ),
        permissions: [PermissionType.RepresentativesView],
        visible: featureAccess.canAccessRepresentatives,
      },
      {
        text: 'Tax Rates',
        route: concatRoute(SetupRoutes.SetupRouter, SetupRoutes.TaxRatesPage),
        permissions: [PermissionType.TaxRatesView],
        visible: featureAccess.canAccessTaxRates,
      },
      {
        text: 'Carriers',
        route: concatRoute(SetupRoutes.SetupRouter, SetupRoutes.CarriersPage),
        permissions: [PermissionType.CarriersView],
        visible: featureAccess.canAccessCarriers,
      },
      {
        text: 'Preferences',
        route: concatRoute(SetupRoutes.SetupRouter, SetupRoutes.SettingsPage),
      },
    ],
    route: null,
  },
  {
    text: 'Remote Support',
    icon: IconNames.MenuRemoteSupport,
    route: concatRoute(
      RemoteSupportRoutes.RemoteSupportRouter,
      RemoteSupportRoutes.RemoteSupportPage
    ),
    subitems: [],
    onlyExecutor: true,
  },
];
